import React, { useState, useEffect, useRef } from 'react'
import Sidebar from '../components/chat/Sidebar'
import TopBar from '../components/chat/TopBar'
import ChatInput from '../components/chat/ChatInput'
import MessageList from '../components/chat/MessageList'
import Welcome from '../components/chat/Welcome'
import Spinner from '../components/common/Spinner'
import { Navigate, useParams, useNavigate } from 'react-router-dom'
import { AuthStatus, useAuth } from '../context/AuthContext'
import {
  getChatResponse,
  createCancelTokenSource,
  DocumentReference,
  DaxQuery,
  getChat,
  MessageData,
} from '../api/chatService'
import { v4 as uuidv4 } from 'uuid'
import axios, { CancelTokenSource } from 'axios'

const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<MessageData[]>([])
  const [isWaiting, setIsWaiting] = useState(false)
  const [chatId, setChatId] = useState<string>(uuidv4())
  const [initialMessage, setInitialMessage] = useState('')
  const [focusTrigger, setFocusTrigger] = useState(0)
  const [cancelTokenSource, setCancelTokenSource] =
    useState<CancelTokenSource | null>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const { user, logout, status } = useAuth()
  const { chatId: urlChatId } = useParams()
  const navigate = useNavigate()
  const sidebarRef = useRef<{
    removeEmptyChatIfExists: (chatId: string) => void
    reloadChats: () => void
  }>(null)

  const handleChatSelect = async (selectedChatId: string) => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Chat changed')
    }

    // Remove current chat if it's empty (no messages)
    if (messages.length === 0 && sidebarRef.current) {
      sidebarRef.current.removeEmptyChatIfExists(chatId)
    }

    try {
      const chatData = await getChat(selectedChatId)
      setChatId(selectedChatId)
      setMessages(chatData.messages)
      setInitialMessage('')
      navigate(`/chats/${selectedChatId}`)
      setIsSidebarOpen(false)
    } catch (error) {
      console.error('Error loading chat:', error)
    }
  }

  // Handle URL parameter changes
  useEffect(() => {
    if (urlChatId && urlChatId !== chatId) {
      handleChatSelect(urlChatId)
    }
  }, [urlChatId, chatId])

  if (status === AuthStatus.LOADING) {
    return <Spinner text="Loading your account..." />
  }
  if (status === AuthStatus.UNAUTHENTICATED || !user) {
    return <Navigate to="/login" />
  }

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false)
  }

  const handleOpenSidebar = () => {
    setIsSidebarOpen(true)
  }

  const fetchChatResponse = async (currentMessages: MessageData[]) => {
    setIsWaiting(true)
    const source = createCancelTokenSource()
    setCancelTokenSource(source)

    // Filter out error messages before sending to the API
    const messagesToSend = currentMessages.filter(
      message => message.role !== 'error'
    )

    // Create a message ID for the bot's response
    let hasAddedMessage = false

    let newResponseId: string | null = null

    try {
      const handleNewResponseId = (responseId: string) => {
        console.log('updating new response id', responseId)
        newResponseId = responseId
      }
      // Function to handle streaming updates
      const handleStreamUpdate = (chunk: string) => {
        if (!newResponseId) {
          // only add if last message is not an error
          if (messages[messages.length - 1].role !== 'error') {
            const errorMessage: MessageData = {
              id: uuidv4(),
              role: 'error',
              content: 'An unexpected error occurred.',
              timestamp: Date.now() / 1000,
            }
            setMessages(prevMessages => [...prevMessages, errorMessage])
          }
        } else {
          console.log('new response id', newResponseId)
          console.log('messages', messages)
          // For the first chunk, create a new message
          if (!hasAddedMessage && chunk.trim()) {
            hasAddedMessage = true
            setIsWaiting(false)

            const botResponse: MessageData = {
              id: newResponseId,
              role: 'assistant',
              content: chunk,
              timestamp: Date.now() / 1000,
              streamingComplete: false,
            }

            setMessages(prevMessages => [...prevMessages, botResponse])
          }
          // For subsequent chunks, update the existing message
          else if (hasAddedMessage) {
            setMessages(prevMessages => {
              return prevMessages.map(msg =>
                msg.id === newResponseId
                  ? { ...msg, content: msg.content + chunk }
                  : msg
              )
            })
          }
        }
      }

      // Function to handle document references
      const handleDocumentsReceived = (documents: DocumentReference[]) => {
        setMessages(prevMessages => {
          return prevMessages.map(msg =>
            msg.id === newResponseId ? { ...msg, documents } : msg
          )
        })
      }

      // Function to handle DAX queries the analytics agent ran
      const handleDaxQueriesReceived = (daxQueries: DaxQuery[]) => {
        setMessages(prevMessages => {
          return prevMessages.map(msg =>
            msg.id === newResponseId ? { ...msg, daxQueries } : msg
          )
        })
      }

      await getChatResponse(
        messagesToSend,
        user.email,
        chatId,
        handleStreamUpdate,
        handleDocumentsReceived,
        handleNewResponseId,
        source,
        handleDaxQueriesReceived
      )

      // Mark streaming as complete
      if (hasAddedMessage) {
        setMessages(prevMessages => {
          return prevMessages.map(msg =>
            msg.id === newResponseId ? { ...msg, streamingComplete: true } : msg
          )
        })

        // Navigate to specific chat URL after first response is complete
        navigate(`/chats/${chatId}`)

        // Reload chats overview after response is completed
        if (sidebarRef.current) {
          sidebarRef.current.reloadChats()
        }
      }

      // If streaming never started (no chunks received), clear loading state
      if (!hasAddedMessage) {
        setIsWaiting(false)
      }
    } catch (error) {
      console.error('Error fetching response:', error)
      // Handle error - show error message to user
      const errorMessage: MessageData = {
        id: newResponseId || uuidv4(),
        role: 'error',
        content:
          error instanceof Error &&
          error.message !== 'Request canceled by user' &&
          error.message !== 'New chat started' &&
          error.message !== 'User signed out'
            ? `Sorry, something went wrong: ${error.message}`
            : 'An unexpected error occurred.', // Default message if error is not an Error instance or is a cancellation
        timestamp: Date.now() / 1000,
      }

      // Only add error message if it's not a cancellation error
      if (
        errorMessage.content !== 'An unexpected error occurred.' ||
        !(error instanceof axios.Cancel)
      ) {
        setMessages(prevMessages => [...prevMessages, errorMessage])
      }

      setIsWaiting(false)
    } finally {
      setCancelTokenSource(null)
    }
  }

  const handleCancelRequest = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Request canceled by user')
    }
  }

  const handleSendMessage = (content: string) => {
    const userMessage: MessageData = {
      id: uuidv4(),
      role: 'user',
      content,
      timestamp: Date.now() / 1000,
    }

    const updatedMessages = [...messages, userMessage]
    setInitialMessage('')
    setMessages(updatedMessages)
    fetchChatResponse(updatedMessages)
  }

  const handleNewChat = () => {
    // If we're already on an empty chat, don't create a new one
    setIsSidebarOpen(false)
    if (messages.length === 0) {
      // Just focus the input to indicate the action was received
      setFocusTrigger(prev => prev + 1)
      return
    }

    if (cancelTokenSource) {
      cancelTokenSource.cancel('New chat started')
    }

    const newChatId = uuidv4()
    setMessages([])
    setChatId(newChatId)
    setFocusTrigger(prev => prev + 1)
    navigate('/chats') // Navigate to /chats instead of /chats/{newChatId}
  }

  const handleExampleClick = (example: string) => {
    setInitialMessage(example)
  }

  const handleSignOut = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('User signed out')
    }
    logout()
  }

  return (
    <div className="chat-page">
      <div className="chat-page__container">
        <TopBar
          onToggleSidebar={handleOpenSidebar}
          isSidebarOpen={isSidebarOpen}
        />

        <Sidebar
          ref={sidebarRef}
          isOpen={isSidebarOpen}
          onClose={handleCloseSidebar}
          onNewChat={handleNewChat}
          onSignOut={handleSignOut}
          onChatSelect={handleChatSelect}
          user={user}
          currentChatId={chatId}
          isNewChatRoute={!urlChatId}
          onChatCreated={chatId => {
            // Add new chat to sidebar when created
            console.log('Chat created:', chatId)
          }}
          onChatDeleted={deletedChatId => {
            // Handle chat deletion - open new chat if current chat was deleted
            if (deletedChatId === chatId) {
              handleNewChat()
            }
          }}
        />

        <div className="chat-page__main">
          <div className="chat-page__content">
            {messages.length === 0 ? (
              <Welcome onExampleClick={handleExampleClick} />
            ) : (
              <MessageList
                messages={messages}
                isWaiting={isWaiting}
                user={user}
                chatId={chatId}
              />
            )}
          </div>

          <ChatInput
            onSendMessage={handleSendMessage}
            onCancelRequest={handleCancelRequest}
            isWaiting={isWaiting}
            initialMessage={initialMessage}
            focusTrigger={focusTrigger}
          />
        </div>
      </div>
    </div>
  )
}

export default ChatPage
